import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  GitBranch, Github, Loader2, Save, RefreshCw, History, CheckCircle2,
  XCircle, PlugZap, Unplug, RotateCcw, Eye, ExternalLink, AlertTriangle, KeyRound,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';

function fmtTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function GitBackupPage() {
  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState(true);

  // connect form
  const [token, setToken] = useState('');
  const [repo, setRepo] = useState('');
  const [branch, setBranch] = useState('main');
  const [prefix, setPrefix] = useState('');
  const [connecting, setConnecting] = useState(false);

  // snapshot
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  // versions
  const [versions, setVersions] = useState(null);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // restore dialog
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const loadConnection = useCallback(async () => {
    setLoading(true);
    try { setConn(await api.gvcConnection()); }
    catch (e) { toast.error(e.message || 'Failed to load connection'); }
    finally { setLoading(false); }
  }, []);

  const loadVersions = useCallback(async () => {
    setLoadingVersions(true);
    try { const r = await api.gvcVersions(); setVersions(r.versions || []); }
    catch (e) { toast.error(e.message || 'Failed to load versions'); setVersions([]); }
    finally { setLoadingVersions(false); }
  }, []);

  useEffect(() => { loadConnection(); }, [loadConnection]);
  useEffect(() => { if (conn?.connected) loadVersions(); }, [conn?.connected, loadVersions]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      await api.gvcConnect({ token, repo, branch, prefix });
      setToken('');
      toast.success('Repository connected');
      await loadConnection();
    } catch (e) { toast.error(e.message || 'Could not connect'); }
    finally { setConnecting(false); }
  };

  const handleDisconnect = async () => {
    try {
      await api.gvcDisconnect();
      toast.success('Disconnected');
      setVersions(null);
      await loadConnection();
    } catch (e) { toast.error(e.message || 'Could not disconnect'); }
  };

  const handleSnapshot = async () => {
    setSaving(true);
    try {
      const r = await api.gvcSnapshot(message);
      toast.success(`Saved version ${r.short}`);
      setMessage('');
      await loadConnection();
      await loadVersions();
    } catch (e) { toast.error(e.message || 'Snapshot failed'); }
    finally { setSaving(false); }
  };

  const openPreview = async (v) => {
    setPreview({ version: v, data: null });
    setPreviewing(true);
    try {
      const data = await api.gvcPreview(v.sha);
      setPreview({ version: v, data });
    } catch (e) {
      toast.error(e.message || 'Preview failed');
      setPreview(null);
    } finally { setPreviewing(false); }
  };

  const handleRestore = async () => {
    if (!preview?.version) return;
    setRestoring(true);
    try {
      const r = await api.gvcRestore(preview.version.sha);
      const parts = Object.entries(r.restored || {}).map(([k, n]) => `${n} ${k}`).join(', ');
      toast.success(`Restored ${preview.version.short}${parts ? ` — ${parts}` : ''}`);
      setPreview(null);
      await loadConnection();
    } catch (e) { toast.error(e.message || 'Restore failed'); }
    finally { setRestoring(false); }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="git-backup-page">
      <div className="flex items-start gap-3">
        <GitBranch className="w-7 h-7 text-primary mt-0.5" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Version Control</h1>
          <p className="text-sm text-muted-foreground">
            Back up this workspace to your own GitHub repository and restore any previous version.
            Covers reports, dashboards, semantic models, glossary, branding and decision policies.
          </p>
        </div>
      </div>

      {/* Connection card */}
      <Card className="p-5 space-y-4" data-testid="gvc-connection-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-medium">
            <Github className="w-4 h-4" /> Connection
          </div>
          {conn?.connected ? (
            <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border-emerald-500/30 gap-1" data-testid="gvc-status-connected">
              <CheckCircle2 className="w-3 h-3" /> Connected
            </Badge>
          ) : (
            <Badge variant="outline" className="gap-1 text-muted-foreground" data-testid="gvc-status-disconnected">
              <XCircle className="w-3 h-3" /> Not connected
            </Badge>
          )}
        </div>

        {conn?.connected ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-muted-foreground">Account</span><div className="font-medium">@{conn.login}</div></div>
              <div><span className="text-muted-foreground">Repository</span><div className="font-medium">{conn.repo}</div></div>
              <div><span className="text-muted-foreground">Branch</span><div className="font-medium">{conn.branch}</div></div>
              <div><span className="text-muted-foreground">Folder</span><div className="font-medium">{conn.prefix || '(repo root)'}</div></div>
            </div>
            <div className="text-xs text-muted-foreground">
              Connected {fmtTime(conn.connected_at)}{conn.connected_by ? ` by ${conn.connected_by}` : ''}.
              {conn.last_snapshot_sha ? ` Last version ${conn.last_snapshot_sha.slice(0, 7)} at ${fmtTime(conn.last_snapshot_at)}.` : ''}
            </div>
            <Button variant="outline" size="sm" onClick={handleDisconnect} data-testid="gvc-disconnect-btn" className="gap-1">
              <Unplug className="w-3.5 h-3.5" /> Disconnect
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs flex items-center gap-1"><KeyRound className="w-3 h-3" /> Fine-grained personal access token</Label>
              <Input type="password" value={token} onChange={(e) => setToken(e.target.value)}
                placeholder="github_pat_..." data-testid="gvc-token-input" autoComplete="off" />
              <p className="text-[11px] text-muted-foreground mt-1">
                Needs <strong>Contents: Read and write</strong> on the target repository. The token is encrypted and never shown again.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Repository (owner/repo)</Label>
                <Input value={repo} onChange={(e) => setRepo(e.target.value)} placeholder="acme/analytics-backup" data-testid="gvc-repo-input" />
              </div>
              <div>
                <Label className="text-xs">Branch</Label>
                <Input value={branch} onChange={(e) => setBranch(e.target.value)} placeholder="main" data-testid="gvc-branch-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Folder prefix (optional)</Label>
              <Input value={prefix} onChange={(e) => setPrefix(e.target.value)} placeholder="e.g. workspaces/acme (blank = repo root)" data-testid="gvc-prefix-input" />
            </div>
            <Button onClick={handleConnect} disabled={connecting || !token || !repo} data-testid="gvc-connect-btn" className="gap-1">
              {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlugZap className="w-4 h-4" />} Connect
            </Button>
          </div>
        )}
      </Card>

      {/* Save version */}
      {conn?.connected && (
        <Card className="p-5 space-y-3" data-testid="gvc-save-card">
          <div className="flex items-center gap-2 font-medium"><Save className="w-4 h-4" /> Save a version</div>
          <p className="text-sm text-muted-foreground">
            Snapshots everything versionable and commits it to <strong>{conn.repo}</strong> on <strong>{conn.branch}</strong>.
          </p>
          <Input value={message} onChange={(e) => setMessage(e.target.value)}
            placeholder="Optional commit message (auto-generated if blank)" data-testid="gvc-message-input" />
          <Button onClick={handleSnapshot} disabled={saving} data-testid="gvc-snapshot-btn" className="gap-1">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save version now
          </Button>
        </Card>
      )}

      {/* Version history */}
      {conn?.connected && (
        <Card className="p-5 space-y-3" data-testid="gvc-history-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-medium"><History className="w-4 h-4" /> Version history</div>
            <Button variant="ghost" size="sm" onClick={loadVersions} disabled={loadingVersions} className="gap-1">
              <RefreshCw className={`w-3.5 h-3.5 ${loadingVersions ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
          {loadingVersions ? (
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          ) : !versions?.length ? (
            <div className="text-sm text-muted-foreground italic py-4 text-center" data-testid="gvc-history-empty">
              No versions yet. Click "Save version now" to create your first snapshot.
            </div>
          ) : (
            <div className="divide-y divide-border rounded-lg border">
              {versions.map((v) => (
                <div key={v.sha} className="flex items-start justify-between gap-3 px-3 py-2.5" data-testid={`gvc-version-${v.short}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{(v.message || '').split('\n')[0]}</div>
                    <div className="text-xs text-muted-foreground">
                      <span className="font-mono">{v.short}</span> · {v.author || 'unknown'} · {fmtTime(v.date)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {v.html_url && (
                      <a href={v.html_url} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-muted text-muted-foreground" title="View on GitHub">
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                    <Button variant="outline" size="sm" className="gap-1" onClick={() => openPreview(v)} data-testid={`gvc-preview-${v.short}`}>
                      <Eye className="w-3.5 h-3.5" /> Preview
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Restore / preview dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent data-testid="gvc-restore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-4 h-4" /> Restore version {preview?.version?.short}
            </DialogTitle>
            <DialogDescription>
              {(preview?.version?.message || '').split('\n')[0]}
            </DialogDescription>
          </DialogHeader>

          {previewing ? (
            <div className="py-6 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Calculating what will change…
            </div>
          ) : preview?.data ? (
            <div className="space-y-3">
              <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-300">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Restoring overwrites the current state of the covered artifact types with this version. This can't be undone (save a version first if unsure).</span>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">What will change</div>
                <ul className="text-sm space-y-1" data-testid="gvc-preview-changes">
                  {preview.data.changes.map((c, i) => (
                    <li key={i} className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{c}</li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)} data-testid="gvc-restore-cancel">Cancel</Button>
            <Button onClick={handleRestore} disabled={restoring || previewing} data-testid="gvc-restore-confirm" className="gap-1">
              {restoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Restore this version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
